/**
 * EnhancedTableHeader - Improved table header with Notion-style column menus
 * Builds on AdvancedTableHeader with better UX and auto-detection features
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Settings, Eye, EyeOff, MoreHorizontal, Filter, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';

import { TableColumn } from '../HorizontalTable';
import { NotionColumnMenu } from './NotionColumnMenu';
import { 
  FilterState, 
  ColumnFilter, 
  FilterValue,
  FilterType,
  createColumnFilter 
} from '../../../utils/tableFilters';
import { 
  SortState, 
  SortDirection, 
  SortDataType,
  getSortDirection,
  getSortPriority
} from '../../../utils/tableSort';

export interface EnhancedTableColumn extends TableColumn {
  // Enhanced properties beyond the base TableColumn
  filterable?: boolean;
  filterType?: FilterType;
  filterOptions?: Array<{ label: string; value: any }>;
  filterPlaceholder?: string;
  
  sortable?: boolean;
  sortDataType?: SortDataType;
  customSort?: (a: any, b: any) => number;
  
  hideable?: boolean;
  resizable?: boolean;
  pinnable?: boolean;
  
  description?: string;
  helpText?: string;
  
  // Data for auto-detection
  data?: unknown[];
}

export interface EnhancedTableHeaderProps {
  columns: EnhancedTableColumn[];
  data: Record<string, any>[];
  filterState: FilterState;
  sortState: SortState;
  columnVisibility: Record<string, boolean>;
  
  // Event handlers
  onColumnResize?: (columnKey: string, newWidth: string) => void;
  onFilterChange?: (columnKey: string, filter: FilterValue | null) => void;
  onSortChange?: (columnKey: string, direction: SortDirection, dataType?: SortDataType) => void;
  onColumnVisibilityChange?: (columnKey: string, visible: boolean) => void;
  onColumnPin?: (columnKey: string, pinned: boolean) => void;
  onMultiSortToggle?: (enabled: boolean) => void;
  onClearAllSorts?: () => void;
  onClearAllFilters?: () => void;
  
  // Configuration
  enableGlobalColumnMenu?: boolean;
  enableResizing?: boolean;
  enableFiltering?: boolean;
  enableSorting?: boolean;
  enableColumnPinning?: boolean;
  
  // UI options
  compactMode?: boolean;
  showFilterIndicators?: boolean;
  showSortIndicators?: boolean;
  
  className?: string;
}

interface ResizeState {
  isResizing: boolean;
  resizingColumnKey: string | null;
  startX: number;
  startWidth: number;
}

/**
 * Enhanced table header with Notion-style interactions
 */
export const EnhancedTableHeader: React.FC<EnhancedTableHeaderProps> = ({
  columns,
  data,
  filterState,
  sortState,
  columnVisibility,
  onColumnResize,
  onFilterChange,
  onSortChange,
  onColumnVisibilityChange,
  onColumnPin,
  onMultiSortToggle,
  onClearAllSorts,
  onClearAllFilters,
  enableGlobalColumnMenu = true,
  enableResizing = true,
  enableFiltering = true,
  enableSorting = true,
  enableColumnPinning = false,
  compactMode = false,
  showFilterIndicators = true,
  showSortIndicators = true,
  className = ''
}) => {
  const [resizeState, setResizeState] = useState<ResizeState>({
    isResizing: false,
    resizingColumnKey: null,
    startX: 0,
    startWidth: 0
  });

  const headerRef = useRef<HTMLTableRowElement>(null);

  // Filter visible columns and sort by pinned status
  const visibleColumns = useMemo(() => {
    return columns
      .filter(col => columnVisibility[col.key] !== false)
      .sort((a, b) => {
        // Pinned columns come first
        const aPinned = a.sticky || false;
        const bPinned = b.sticky || false;
        if (aPinned && !bPinned) return -1;
        if (!aPinned && bPinned) return 1;
        return 0;
      });
  }, [columns, columnVisibility]);

  // Extract column data for auto-detection
  const getColumnData = useCallback((columnKey: string) => {
    if (!data || data.length === 0) return [];
    return data.map(row => row[columnKey]).filter(v => v != null);
  }, [data]);

  // Handle mouse down on resize handle
  const handleMouseDown = useCallback((e: React.MouseEvent, columnKey: string, currentWidth: number) => {
    if (!enableResizing || !onColumnResize) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    setResizeState({
      isResizing: true,
      resizingColumnKey: columnKey,
      startX: e.clientX,
      startWidth: currentWidth
    });
  }, [enableResizing, onColumnResize]);

  // Handle mouse move during resize
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!resizeState.isResizing || !resizeState.resizingColumnKey || !onColumnResize) return;

    const deltaX = e.clientX - resizeState.startX;
    const newWidth = Math.max(80, Math.min(800, resizeState.startWidth + deltaX));
    
    onColumnResize(resizeState.resizingColumnKey, `${newWidth}px`);
  }, [resizeState, onColumnResize]);

  // Handle mouse up to end resize
  const handleMouseUp = useCallback(() => {
    setResizeState({
      isResizing: false,
      resizingColumnKey: null,
      startX: 0,
      startWidth: 0
    });
  }, []);

  // Add global event listeners for resize
  useEffect(() => {
    if (resizeState.isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [resizeState.isResizing, handleMouseMove, handleMouseUp]);

  // Parse width value to number
  const parseWidth = (width?: string): number => {
    if (!width) return 150;
    const numValue = parseFloat(width.replace('px', ''));
    return isNaN(numValue) ? 150 : numValue;
  };

  // Get column filter
  const getColumnFilter = useCallback((column: EnhancedTableColumn): ColumnFilter | undefined => {
    return filterState.filters[column.key];
  }, [filterState.filters]);

  // Handle filter change
  const handleFilterChange = useCallback((columnKey: string, filter: FilterValue | null) => {
    if (!onFilterChange) return;
    onFilterChange(columnKey, filter);
  }, [onFilterChange]);

  // Handle sort change
  const handleSortChange = useCallback((columnKey: string, direction: SortDirection, dataType?: SortDataType) => {
    if (!onSortChange) return;
    onSortChange(columnKey, direction, dataType);
  }, [onSortChange]);

  // Handle column pin toggle
  const handleColumnPin = useCallback((columnKey: string, pinned: boolean) => {
    if (!onColumnPin) return;
    onColumnPin(columnKey, pinned);
  }, [onColumnPin]);

  return (
    <thead className={cn(
      "bg-muted/50 border-b border-border sticky top-0 z-10",
      compactMode && "text-xs",
      className
    )}>
      <tr ref={headerRef}>
        {visibleColumns.map((column, index) => {
          const isLastColumn = index === visibleColumns.length - 1;
          const currentWidth = parseWidth(column.width);
          const sortDirection = getSortDirection(sortState, column.key);
          const sortPriority = getSortPriority(sortState, column.key);
          const columnFilter = getColumnFilter(column);
          const hasFilter = columnFilter?.value !== null && columnFilter?.value !== undefined;
          const columnData = column.data || getColumnData(column.key);
          const isPinned = column.sticky || false;
          
          return (
            <th
              key={column.key}
              className={cn(
                "px-4 py-3 text-center text-xs font-medium text-muted-foreground tracking-wider relative group border-r border-border/50 last:border-r-0",
                column.sticky && "sticky left-0 bg-muted/50 z-20 border-r-border",
                compactMode && "px-2 py-2",
                column.className
              )}
              style={{ 
                width: column.width, 
                minWidth: compactMode ? '60px' : '80px',
                maxWidth: '400px'
              }}
            >
              {/* Column content container */}
              <div className="flex flex-col gap-1">
                {/* Header row with label and controls */}
                <div className="flex items-center justify-between gap-2 min-h-[20px]">
                  {/* Column label */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className={cn(
                      "truncate font-medium",
                      (hasFilter || sortDirection) && "text-primary"
                    )}>
                      {column.label}
                    </span>
                    
                    {/* Status indicators */}
                    <div className="flex items-center gap-1">
                      {showSortIndicators && sortDirection && (
                        <div className="flex items-center gap-1">
                          <ArrowUpDown className="h-3 w-3 text-primary" />
                          {sortPriority && sortPriority > 1 && (
                            <Badge variant="secondary" className="h-4 w-4 p-0 text-xs flex items-center justify-center">
                              {sortPriority}
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {showFilterIndicators && hasFilter && (
                        <Filter className="h-3 w-3 text-primary" />
                      )}
                    </div>
                  </div>

                  {/* Notion-style column menu */}
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <NotionColumnMenu
                      column={{
                        ...column,
                        filterType: column.filterType,
                        filterOptions: column.filterOptions,
                        sortDataType: column.sortDataType
                      }}
                      hasFilter={hasFilter}
                      filterValue={columnFilter?.value || null}
                      sortDirection={sortDirection}
                      sortPriority={sortPriority}
                      isPinned={isPinned}
                      isVisible={true}
                      columnData={columnData}
                      onFilterChange={(filter) => handleFilterChange(column.key, filter)}
                      onSortChange={(direction, dataType) => handleSortChange(column.key, direction, dataType)}
                      onVisibilityChange={(visible) => onColumnVisibilityChange?.(column.key, visible)}
                      onPinChange={enableColumnPinning ? (pinned) => handleColumnPin(column.key, pinned) : undefined}
                      onClearFilter={() => handleFilterChange(column.key, null)}
                      onClearSort={() => handleSortChange(column.key, null)}
                      enableFiltering={enableFiltering && column.filterable !== false}
                      enableSorting={enableSorting && column.sortable !== false}
                      enableVisibilityToggle={column.hideable !== false}
                      enablePinning={enableColumnPinning && column.pinnable !== false}
                    />
                  </div>
                </div>

                {/* Quick status row for active filters/sorts in compact mode */}
                {compactMode && (hasFilter || sortDirection) && (
                  <div className="flex items-center gap-1 text-xs">
                    {hasFilter && (
                      <Badge variant="secondary" className="h-4 px-1 text-xs">
                        Filtered
                      </Badge>
                    )}
                    {sortDirection && (
                      <Badge variant="outline" className="h-4 px-1 text-xs">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                        {sortPriority && sortPriority > 1 && ` ${sortPriority}`}
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Resize handle */}
              {enableResizing && 
               column.resizable !== false && 
               !isLastColumn && 
               onColumnResize && (
                <div
                  className={cn(
                    "absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-primary/30 transition-colors group-hover:bg-border",
                    resizeState.resizingColumnKey === column.key && "bg-primary/50"
                  )}
                  onMouseDown={(e) => handleMouseDown(e, column.key, currentWidth)}
                  title="Drag to resize column"
                >
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-0.5 h-4 bg-current opacity-50" />
                </div>
              )}

              {/* Active resize indicator */}
              {resizeState.resizingColumnKey === column.key && (
                <div className="absolute inset-0 bg-primary/10 pointer-events-none" />
              )}
            </th>
          );
        })}

        {/* Global column management */}
        {enableGlobalColumnMenu && onColumnVisibilityChange && (
          <th className="w-12 px-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <Settings className="h-4 w-4" />
                  <span className="sr-only">Column settings</span>
                </Button>
              </DropdownMenuTrigger>
              
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Manage Columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {/* Column visibility toggles */}
                {columns.map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={columnVisibility[column.key] !== false}
                    onCheckedChange={(checked) => 
                      onColumnVisibilityChange(column.key, checked)
                    }
                    disabled={column.hideable === false}
                  >
                    <span className="truncate">{column.label}</span>
                    {column.sticky && (
                      <Badge variant="outline" className="ml-2 text-xs">Pinned</Badge>
                    )}
                  </DropdownMenuCheckboxItem>
                ))}
                
                <DropdownMenuSeparator />
                
                {/* Global actions */}
                {onClearAllFilters && (
                  <DropdownMenuItem onClick={onClearAllFilters}>
                    <Filter className="h-4 w-4 mr-2" />
                    Clear All Filters
                  </DropdownMenuItem>
                )}
                
                {onClearAllSorts && (
                  <DropdownMenuItem onClick={onClearAllSorts}>
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    Clear All Sorts
                  </DropdownMenuItem>
                )}

                {/* Multi-sort toggle */}
                {onMultiSortToggle && (
                  <DropdownMenuItem onClick={() => onMultiSortToggle(!sortState.multiSort)}>
                    <ArrowUpDown className="h-4 w-4 mr-2" />
                    {sortState.multiSort ? 'Disable' : 'Enable'} Multi-Sort
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </th>
        )}
      </tr>
    </thead>
  );
};

export default EnhancedTableHeader;